"use client";

import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <>
        <div className="hidden md:flex gap-1">
          {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 bg-muted/30 animate-pulse rounded-md" />)}
        </div>
        <div className="md:hidden">
          <div className="h-9 w-9 bg-muted/30 animate-pulse rounded-md" />
        </div>
      </>;
  }
  const filteredMenuItems = menuItems.filter(item => item.label !== "About Us");
  return <>
      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {filteredMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild className="hover:!bg-transparent focus:!bg-transparent data-[active=true]:bg-transparent">
                <Link to={item.href || "#"} className="text-base font-medium text-black hover:text-black transition-colors duration-[var(--duration-fast)] px-3 py-2 relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-primary after:transition-all after:duration-[var(--duration-medium)] hover:after:w-full">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background border-b shadow-medium md:hidden z-50">
          <nav className="flex flex-col py-4 px-6 gap-2">
            {filteredMenuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-base font-medium text-black hover:text-black transition-colors duration-[var(--duration-fast)] py-2" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 w-full bg-background/95 backdrop-blur-sm border-b border-border z-50 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out)] ${scrolled ? "h-16 shadow-soft" : "h-22"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 flex-shrink-0 hover:opacity-90 transition-opacity duration-[var(--duration-fast)]">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1515015/img/clearconnect_logo.png" alt="ClearConnect Logo" className="h-7" />
        </Link>

        <MenuProvider menu_id="4" className="flex-1 flex justify-center">
          <HeaderMenuConsumer />
        </MenuProvider>

        <Button asChild className="ml-auto text-base hover:!bg-primary-hover focus:!bg-primary-hover hover:shadow-medium hover:scale-105 active:scale-100 transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
          <Link to="/DemoPage">Demo anfordern</Link>
        </Button>
      </div>
    </header>;
}